import { useLayoutEffect, useRef, useState } from "react";

import { cn } from "../../lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

/**
 * A component that truncates text and shows a tooltip ONLY on actual overflow.
 *
 * @param {string} text - The text to display
 * @param {number} maxLength - Maximum length before truncation (character-based)
 * @param {number} lines - Number of lines to show before truncation (line-clamp)
 * @param {string} className - Optional container className
 */
const TruncatedText = ({ text, maxLength = 35, lines = 0, className }) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);

  useLayoutEffect(() => {
    const checkOverflow = () => {
      const element = textRef.current;
      if (element) {
        // For multi-line clamp, we check scrollHeight vs offsetHeight
        // For single-line truncate, we check scrollWidth vs offsetWidth
        const hasOverflow = lines > 0 ? element.scrollHeight > element.offsetHeight : element.scrollWidth > element.offsetWidth;

        setIsOverflowing(hasOverflow);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [text, lines]);

  if (!text) return null;

  const isLineClamp = lines > 0;

  const content = (
    <span
      ref={textRef}
      className={cn(
        "text-xs transition-all duration-200",
        !isLineClamp && "whitespace-nowrap block truncate",
        isLineClamp && `line-clamp-${lines} whitespace-normal leading-relaxed`,
        isOverflowing && "cursor-help border-b border-transparent hover:border-slate-300 dark:hover:border-slate-600",
        className
      )}
    >
      {isLineClamp ? text : text.length > maxLength ? `${text.substring(0, maxLength)}...` : text}
    </span>
  );

  if (!isOverflowing) {
    return content;
  }

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-xs sm:max-w-md break-words p-4 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-2xl border border-slate-200 dark:border-slate-800 rounded-xl z-[100]"
      >
        <p className="text-sm leading-relaxed font-medium">{text}</p>
      </TooltipContent>
    </Tooltip>
  );
};

export default TruncatedText;
